import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  SomaIcon,
  SomaPopover,
  SomaButton,
} from '@soma/react';
import Text from 'xpi-black-ui/Base/Typography/Text';
import Button from 'xpi-black-ui/Base/Button';
import OptionsList from 'xpi-black-ui/Common/OptionsList';
import GenericInput from '../../Generics/GenericInput';
import NumberBadge from '../../Utils/NumberBadge';
import WrapperDialog, {
  WrapperHeader,
  WrapperDialogContent,
  WrapperDialogContentSeparator,
  WrapperDialogContentLegs,
  WrapperDialogContentInformation,
  WrapperSection,
  WrapperFooter,
  WrapperFooterContent,
  WrapperFooterInfo,
  WrapperPositionInformation,
  WrapperUnwindingInformation,
  WrapperLegInformation,
  WrapperSomaPopoverContent,
  AcceptTermButton,
  WrapperLegInfo
} from './styles';

export const structures = ['Call', 'Call Spread','Put', 'Put Spread', 'Call UO', 'PUT DO'];

export default function UnwindingDialog({ onAcceptTerm, isLoading, onClose, position, isOpen }) {
  const [isOpenPopover, setIsOpenPopover] = useState(false);
  const [spread, setSpread] = useState('0,00');
  const [comission, setComission] = useState('0,00');
  const [currentPositionTotalValueAfterComission, setCurrentPositionTotalValueAfterComission] = useState('R$ 0,00');
  const [grossGainAfterCommission, setGrossGainAfterCommission] = useState('R$ 0,00');
  const [sellAssetOptions, setSellAssetOptions] = useState([
    { id: 0, label: 'Com Venda do Ativo', isSelected: false },
    { id: 1, label: 'Sem Venda do Ativo', isSelected: false }
  ]);

  const formatCurrencyStringToNumber = useCallback((value) => +(value?.replaceAll('R$', '')?.replaceAll('.', '')?.replaceAll(',', '.')?.replace(/\s/g, '')) || 0.00, []);
  const formatStringToPercentageNumber = useCallback((value) => +(value?.replaceAll(',', '.')?.replace(/\s/g, '')) || 0.00, []);
  const formatNumberToPercentageString = useCallback((value) => value?.toFixed(2)?.replaceAll('.', ',')?.replace(/\s/g, '') || '0.00', []);
  const formatNumberToCurrencyString = useCallback((value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 })?.format(value) || 'R$ 0,00', []);

  const hasSellAssetOptionsSelected = useMemo(() => sellAssetOptions.filter(option => option.isSelected)?.length > 0, [sellAssetOptions]);
  const isSellAssetOptionsSelected = useMemo(() => sellAssetOptions.filter(option => option.isSelected)[0]?.id === 0, [sellAssetOptions]);
  const isCurrentProfitWithSellAsset = useMemo(() => (position?.structure?.hasAsset && isSellAssetOptionsSelected) || (position?.structure?.hasAsset && !hasSellAssetOptionsSelected), [position?.structure?.hasAsset, isSellAssetOptionsSelected, hasSellAssetOptionsSelected]);

  const acquisitionInfo = useMemo(() => {
    const ACQUISITON_INFO_CURRENCY = position?.acquisitionInfo[isCurrentProfitWithSellAsset ? 'costWithAsset' : 'costWithoutAsset']
    const ACQUISITON_INFO_CURRENCY_IN_NUMBER = formatCurrencyStringToNumber(ACQUISITON_INFO_CURRENCY);
    const ACQUISITON_INFO_CURRENCY_INVERTED = formatNumberToCurrencyString(ACQUISITON_INFO_CURRENCY_IN_NUMBER === 0 ? 0 : ACQUISITON_INFO_CURRENCY_IN_NUMBER * -1.00);

    return {
      currencyValue: ACQUISITON_INFO_CURRENCY,
      value: ACQUISITON_INFO_CURRENCY_IN_NUMBER,
      currencyInvertedValue: ACQUISITON_INFO_CURRENCY_INVERTED
    };

  }, [position?.acquisitionInfo, isCurrentProfitWithSellAsset])

  const currentGrossResult = useMemo(() => {
    const ONLY_WITH_OPTION_VALUE = position?.currentPosition?.onlyWithOptionValue;
    const ONLY_WITH_OPTION_VALUE_FORMATTED_VALUE = formatCurrencyStringToNumber(ONLY_WITH_OPTION_VALUE);

    const CURRENT_PROFIT_VALUE_WITH_OR_WITHOUT_VALUE = isCurrentProfitWithSellAsset ? position?.currentPosition?.profit?.withAsset : position?.currentPosition?.profit?.withoutAsset;
    const CURRENT_PROFIT_VALUE_WITH_OR_WITHOUT_FORMATTED_VALUE = formatCurrencyStringToNumber(CURRENT_PROFIT_VALUE_WITH_OR_WITHOUT_VALUE);
    const COMISSION_FEE_GAIN_OR_LOSS = position?.commissionFee[CURRENT_PROFIT_VALUE_WITH_OR_WITHOUT_FORMATTED_VALUE >= 0 ? 'onGain' : 'onLoss'];

    return {
      value: {
        string: formatNumberToCurrencyString(CURRENT_PROFIT_VALUE_WITH_OR_WITHOUT_FORMATTED_VALUE),
        number: CURRENT_PROFIT_VALUE_WITH_OR_WITHOUT_FORMATTED_VALUE
      },
      valueOnlyWithOptions: {
        string: formatNumberToCurrencyString(ONLY_WITH_OPTION_VALUE_FORMATTED_VALUE),
        number: ONLY_WITH_OPTION_VALUE_FORMATTED_VALUE
      },
      feePercentage: {
        min: COMISSION_FEE_GAIN_OR_LOSS?.minPercentage || 0,
        max: COMISSION_FEE_GAIN_OR_LOSS?.maxPercentage || 0,
        default: COMISSION_FEE_GAIN_OR_LOSS?.defaultPercentage || 0,
      },
    };
  }, [position?.currentPosition?.profit?.withAsset, position?.currentPosition?.profit?.withoutAsset, position?.commissionFee, isCurrentProfitWithSellAsset]);

  useEffect(() => {
    const NEW_COMISSION_FORMATTED_VALUE = formatNumberToPercentageString(currentGrossResult?.feePercentage.max)
    setSpread(NEW_COMISSION_FORMATTED_VALUE)
    updateComission(NEW_COMISSION_FORMATTED_VALUE)

  }, [isCurrentProfitWithSellAsset])

  const isSpreadWithinRange = useMemo(() => {
    const SPREAD_CONVERTED_VALUE = formatStringToPercentageNumber(spread);
    const IS_SPREAD_WITHIN_RANGE = SPREAD_CONVERTED_VALUE >= currentGrossResult?.feePercentage?.min && SPREAD_CONVERTED_VALUE <= currentGrossResult?.feePercentage?.max;

    return IS_SPREAD_WITHIN_RANGE;
  }, [spread, currentGrossResult]);

  const isDisabledUnwinding = useMemo(() => {

    const HAS_SELL_ASSET = position?.structure?.hasAsset;
    const HAS_SELL_ASSET_OPTIONS_SELECTED = hasSellAssetOptionsSelected;

    return isLoading || (HAS_SELL_ASSET
      ? !(HAS_SELL_ASSET && HAS_SELL_ASSET_OPTIONS_SELECTED && isSpreadWithinRange)
      : !(isSpreadWithinRange));
  }, [position?.structure?.hasAsset, hasSellAssetOptionsSelected, isSpreadWithinRange]);

  const getLegInformation = useCallback(({ optionType, barrierType, currentBarrier, strike, side, quantityCurrent }) => {
    const IS_SPOT = optionType.toUpperCase() === 'SPOT';
    const STRIKE_TITLE = IS_SPOT ? '' : `Strike ${strike.percentage} / ${strike.value}`;
    const BARRIER_TITLE = barrierType ? `Barreira ${barrierType} ${currentBarrier.percentage} / ${currentBarrier.value}` : '';

    return {
      option: `${side} ${optionType} - Qtde Atual: ${quantityCurrent}`,
      strike: STRIKE_TITLE,
      barrier: BARRIER_TITLE
    }
  }, []);


  const updateGrossGainAfterComission = useCallback((comissionCalculed) => {
    const CURRENT_GROSS_GAIN_OR_LOSS_VALUE = currentGrossResult.value.number;
    const NEW_FORMATTED_VALUE = formatNumberToCurrencyString(comissionCalculed >= 0 ? CURRENT_GROSS_GAIN_OR_LOSS_VALUE - comissionCalculed : CURRENT_GROSS_GAIN_OR_LOSS_VALUE + comissionCalculed)

    setGrossGainAfterCommission(NEW_FORMATTED_VALUE);
  }, [isSellAssetOptionsSelected, currentGrossResult.value.number]);

  const updateCurrentPositionTotalAfterComission = useCallback((comissionCalculed) => {
    const CURRENT_POSITION_TOTAL_CURRENCY = isCurrentProfitWithSellAsset ? position?.currentPosition?.totalValue : position?.currentPosition?.onlyWithOptionValue;
    const CURRENT_POSITION_TOTAL_VALUE = formatCurrencyStringToNumber(CURRENT_POSITION_TOTAL_CURRENCY);
    const NEW_CURRENT_POSITION_TOTAL_VALUE = formatNumberToCurrencyString(comissionCalculed >= 0 ? CURRENT_POSITION_TOTAL_VALUE - comissionCalculed : CURRENT_POSITION_TOTAL_VALUE + comissionCalculed);

    setCurrentPositionTotalValueAfterComission(NEW_CURRENT_POSITION_TOTAL_VALUE);

  }, [isSellAssetOptionsSelected, currentGrossResult.value.number]);

  const cleanSellAssetOptionsSelection = useCallback(() => {
    const NEW_SELL_ASSET_OPTIONS = sellAssetOptions.map(assetOption => {
      assetOption.isSelected = false;

      return assetOption;
    });

    setSellAssetOptions(NEW_SELL_ASSET_OPTIONS);
  }, [sellAssetOptions]);

  const onChangeSpread = useCallback((inputedSpread) => {
    updateComission(inputedSpread)
  }, [currentGrossResult.value.number]);

  const onInit = useCallback(() => {
    const HAS_CURRENT_GROSS_FEE_VALUE = currentGrossResult?.feePercentage?.default;
    const HAS_CURRENT_GROSS_STRING_VALUE = currentGrossResult?.value?.string;

    if (HAS_CURRENT_GROSS_FEE_VALUE && HAS_CURRENT_GROSS_STRING_VALUE) {
      const INITIAL_SPREAD_FORMATTED_VALUE = formatNumberToPercentageString(currentGrossResult?.feePercentage?.default);

      onChangeSpread(INITIAL_SPREAD_FORMATTED_VALUE);
    }
  }, [position, sellAssetOptions, currentGrossResult?.feePercentage?.default, currentGrossResult?.gainOrLossValue?.string, onChangeSpread]);

  const handleOnClose = useCallback(() => {
    cleanSellAssetOptionsSelection();
    onClose();
  }, []);

  function updateComission(inputedSpread)
  {
    const INPUTED_SPREAD_FORMATTED_VALUE = formatStringToPercentageNumber(inputedSpread);
    const NEW_INPUTED_SPREAD_FORMATTED_VALUE = formatNumberToPercentageString(INPUTED_SPREAD_FORMATTED_VALUE);

    var NEW_COMISSION_CALCULED_VALUE = ((Math.abs(currentGrossResult.valueOnlyWithOptions.number) * INPUTED_SPREAD_FORMATTED_VALUE) / 100);
    if(position?.structure?.hasAsset)
    {
      NEW_COMISSION_CALCULED_VALUE = ((Math.abs(currentGrossResult.value.number) * INPUTED_SPREAD_FORMATTED_VALUE) / 100);
    }

    const NEW_COMISSION_FORMATTED_VALUE = formatNumberToCurrencyString(NEW_COMISSION_CALCULED_VALUE);

    setSpread(NEW_INPUTED_SPREAD_FORMATTED_VALUE);
    setComission(NEW_COMISSION_FORMATTED_VALUE);
    updateGrossGainAfterComission(NEW_COMISSION_CALCULED_VALUE);
    updateCurrentPositionTotalAfterComission(NEW_COMISSION_CALCULED_VALUE);

    if (structures.filter((item) => item.toLowerCase() === position?.structure?.name.toLowerCase()).length > 0) {
      const CURRENT_POSITION_TOTAL_CURRENCY = isCurrentProfitWithSellAsset ? position?.currentPosition?.totalValue : position?.currentPosition?.onlyWithOptionValue;
      const CURRENT_POSITION_TOTAL_VALUE = formatCurrencyStringToNumber(CURRENT_POSITION_TOTAL_CURRENCY);
      const NEW_CURRENT_POSITION_TOTAL_VALUE = formatNumberToCurrencyString(NEW_COMISSION_CALCULED_VALUE >= 0 ? CURRENT_POSITION_TOTAL_VALUE - NEW_COMISSION_CALCULED_VALUE : CURRENT_POSITION_TOTAL_VALUE + NEW_COMISSION_CALCULED_VALUE);

      const COMISSION_AWARD = (
        (
          Math.abs(
            currentGrossResult.value.number >= 0
              ? currentGrossResult.value.number
              : CURRENT_POSITION_TOTAL_VALUE
          ) * INPUTED_SPREAD_FORMATTED_VALUE
        ) / 100);

      const COMISSION_AWARD_FORMATTED = formatNumberToCurrencyString(COMISSION_AWARD);
      setComission(COMISSION_AWARD_FORMATTED);

      const GROSS_GRAIN_AFTER_COMISSION = currentGrossResult.value.number >= 0
        ? currentGrossResult.value.number - COMISSION_AWARD
        : currentGrossResult.value.number + COMISSION_AWARD;

      NEW_COMISSION_CALCULED_VALUE = COMISSION_AWARD;
    }
    updateGrossGainAfterComission(NEW_COMISSION_CALCULED_VALUE);
    updateCurrentPositionTotalAfterComission(NEW_COMISSION_CALCULED_VALUE);
  }

  const onChangeComission = useCallback((inputedComission) => {
    const COMISSION_FORMATTED_VALUE = formatCurrencyStringToNumber(inputedComission);
    const NEW_SPREAD_PERCENTAGE_CALCULED_VALUE = formatNumberToPercentageString(Math.abs(((COMISSION_FORMATTED_VALUE / currentGrossResult.value.number) * 100)));

    setComission(inputedComission);
    setSpread(NEW_SPREAD_PERCENTAGE_CALCULED_VALUE);
    updateGrossGainAfterComission(COMISSION_FORMATTED_VALUE);
    updateCurrentPositionTotalAfterComission(COMISSION_FORMATTED_VALUE);
  }, [currentGrossResult.value.number]);

  const onFilterSellAssetOptions = useCallback((option) => {
    const NEW_SELL_ASSET_OPTIONS = sellAssetOptions.map(assetOption => {
      assetOption.isSelected = assetOption.id === option.id

      return assetOption;
    });

    setSellAssetOptions(NEW_SELL_ASSET_OPTIONS);
  }, [sellAssetOptions]);

  useEffect(() => {
    onInit();
    cleanSellAssetOptionsSelection();
  }, [position]);

  const PositionInformation = useCallback(({ title, value, isFullWidth }) => (
    <WrapperPositionInformation isFullWidth={isFullWidth}>
      <GenericInput title={title} defaultValue={`${value}`} isReadOnly />
    </WrapperPositionInformation>
  ), []);

  const UnwindingInformation = useCallback(({ onChange, title, value, isInvalid = false, isPercentage = false, hasNegativeNumber = true }) => (
    <WrapperUnwindingInformation isFullWidth={!position?.structure?.hasAsset}>
      <GenericInput
        onChange={onChange}
        title={title}
        defaultValue={value}
        isInvalid={isInvalid}
        isPercentage={isPercentage}
        hasNegativeNumber={hasNegativeNumber}
      />
    </WrapperUnwindingInformation>
  ), [position?.structure?.hasAsset]);

  const LegInformation = useCallback(({ index, info }) => (
    <WrapperLegInformation>
      <NumberBadge value={index + 1} />
      <WrapperLegInfo>
        <Text bigger>{info.option}</Text>
        {info.strike && <Text bigger>{info.strike}</Text>}
        {info.barrier && <Text bigger>{info.barrier}</Text>}
      </WrapperLegInfo>
    </WrapperLegInformation>
  ), []);

  const LegFixedInformation = useCallback(({ index, value }) => (
    <WrapperLegInformation>
      <NumberBadge value={index} />
      <WrapperLegInfo>
        <Text bigger>{value}</Text>
      </WrapperLegInfo>
    </WrapperLegInformation>
  ), []);

  return (
    <WrapperDialog overlayClick={handleOnClose} open={isOpen}>
      <WrapperHeader onClose={handleOnClose} title={`Antecipar Posição (#${position?.operationId})`} />

      <WrapperDialogContent>
        <WrapperDialogContentSeparator>
          <WrapperSection>
            <WrapperDialogContentLegs>
              {position?.legs?.map(({
                barrierType,
                currentBarrier,
                optionType,
                side,
                strike,
                quantityCurrent,
              }, index) => (
                <LegInformation
                  key={`${optionType}-${side}`}
                  index={index}
                  info={getLegInformation({
                    side,
                    optionType,
                    barrierType,
                    currentBarrier,
                    strike,
                    quantityCurrent
                  })}
                />
              ))}
              <LegFixedInformation key='fixing' index={position?.legs?.length + 1} value={`Data de Encerramento - ${position?.maturityDate}`} />
            </WrapperDialogContentLegs>

            <WrapperDialogContentInformation>
              <PositionInformation title="Cliente" value={position?.clientId} />
              <PositionInformation title="Ativo" value={position?.asset?.ticker} />

              <PositionInformation title="Estrutura" value={position?.structure?.name} />
              <PositionInformation title="Quantidade Contratada" value={position?.quantityContracted} />

              <PositionInformation title="Preço Atual Ativo" value={position?.asset?.currentSpotValue} isFullWidth />

              <PositionInformation
                title={`Valor na Entrada (${isCurrentProfitWithSellAsset ? 'Ações + Opções' : 'Opções' })`}
                value={acquisitionInfo.currencyInvertedValue}
                isFullWidth
              />

              {isCurrentProfitWithSellAsset
                ? (<PositionInformation title="Financeiro Atual de Saída (Ações + Opções)" value={currentPositionTotalValueAfterComission} isFullWidth />)
                : (<PositionInformation title="Financeiro Atual de Saída (Opções)" value={currentPositionTotalValueAfterComission} isFullWidth />)
              }

              <PositionInformation title="Res. Bruto Atual (R$)" value={grossGainAfterCommission} isFullWidth={(!isCurrentProfitWithSellAsset)} />
              {isCurrentProfitWithSellAsset && <PositionInformation title="Total de Proventos" value={position?.asset?.totalDividends} />}

            </WrapperDialogContentInformation>
          </WrapperSection>

          {position?.unwindingDisabled && <Text>Não é possível realizar antecipação após às {position?.unwindingLimitTime}.</Text>}
          {!position?.unwindingDisabled &&
            <WrapperFooter>
              <WrapperFooterContent>
                {position?.structure?.hasAsset && (
                  <OptionsList
                    onFilter={onFilterSellAssetOptions}
                    options={sellAssetOptions}
                  />
                )}

                <UnwindingInformation
                  onChange={onChangeSpread}
                  title={`Spread (Mín: ${currentGrossResult?.feePercentage?.min}% - Máx: ${currentGrossResult?.feePercentage?.max}%)`}
                  value={spread}
                  isInvalid={!isSpreadWithinRange}
                  hasNegativeNumber={false}
                  isPercentage
                />

                <UnwindingInformation
                  onChange={onChangeComission}
                  title="Comissão (R$)"
                  value={comission}
                  hasNegativeNumber={false}
                />

                <SomaPopover
                  onSomaOpen={() => setIsOpenPopover(true)}
                  onSomaClose={() => setIsOpenPopover(false)}
                  label="Aceite de Termos"
                  placement="top-center"
                  open={isOpenPopover}
                >
                  {position?.isAdvisorAuthorizedByModule &&
                    <SomaButton
                      size="sm"
                      onSomaClick={() => setIsOpenPopover(true)}
                      disabled={isDisabledUnwinding}
                      loading={isLoading}
                    >
                      Antecipar
                    </SomaButton>
                  }

                  <WrapperSomaPopoverContent>
                    <div>
                      Você tem certeza que deseja encaminhar a ordem
                      {position?.structure?.hasAsset && <span>{isSellAssetOptionsSelected ? ' COM VENDA DO ATIVO e ' : ' SEM VENDA DO ATIVO e '}</span>}
                      <span> COM SPREAD DE {spread}% ?</span>
                    </div>

                    Lembrando que o PUSH terá validade de 15 minutos, após esse período ele será automaticamente cancelado.

                    <AcceptTermButton onClick={() => {
                      onAcceptTerm({
                        operationId: position?.operationId,
                        quantity: position?.quantityContracted,
                        comissionFeePercentage: formatStringToPercentageNumber(spread),
                        sellAsset: isSellAssetOptionsSelected,
                      })
                    }}
                    >
                      CONTINUAR
                    </AcceptTermButton>
                  </WrapperSomaPopoverContent>
                </SomaPopover>
              </WrapperFooterContent>
              <WrapperFooterInfo>
                {!position?.structure?.hasAsset &&
                  (
                    structures.filter(
                      (item) => item.toLowerCase() === position?.structure?.name.toLowerCase()
                    ).length > 0
                      ? formatCurrencyStringToNumber(grossGainAfterCommission) < 0 && (
                        <>
                          <SomaIcon icon="info" size="sm" color="#FFC709" style={{ marginLeft: '4px', bottom: '-3px' }} />
                          <Text style={{marginLeft: '10px'}}>
                            A comissão nas estruturas de prêmio no prejuízo é calculada com base no "Financeiro Atual de Saída (Opções)".
                          </Text>
                        </>
                      )
                      : (
                        <>
                          <SomaIcon icon="info" size="sm" color="#FFC709" style={{ marginLeft: '4px', bottom: '-3px' }} />
                          <Text style={{marginLeft: '10px'}}>
                            A comissão nas estruturas de prêmio é calculada com base no "Financeiro Atual de Saída (Opções)".
                          </Text>
                        </>
                      )
                  )
                }
              </WrapperFooterInfo>

            </WrapperFooter>
          }

        </WrapperDialogContentSeparator>
      </WrapperDialogContent>
    </WrapperDialog>
  );
}
